export const ONBOARDING_TARGETS = {
  settingsCog: 'settings-cog',
  accountsWidget: 'accounts-widget',
  marketSearch: 'market-search',
  orderbook: 'orderbook',
  marketBlock: 'market-block',
  marketsGridLauncher: 'markets-grid-launcher',
  predictionTradeStatusBar: 'prediction-trade-status-bar',
  newsWidget: 'news-widget',
  newsListHeader: 'news-list-header',
  newsListEditAction: 'news-list-edit-action',
  editListModal: 'edit-list-modal',
  editListHighlight: 'edit-list-highlight',
  marketMatchingButton: 'market-matching-button',
  marketMatchingModal: 'market-matching-modal',
  marketMatchingMatchedOnly: 'market-matching-matched-only',
  settingsCredentialsCard: 'settings-credentials-card',
  addCredentialButton: 'add-credential-button',
  addTradingButton: 'add-trading-button',
  connectWidgetsLinkButton: 'connect-widgets-link-button',
  widgetTweaksButton: 'widget-tweaks-button',
  widgetTweaksPopover: 'widget-tweaks-popover',
  connectWidgetsPopover: 'connect-widgets-popover',
} as const;

export type OnboardingTarget =
  (typeof ONBOARDING_TARGETS)[keyof typeof ONBOARDING_TARGETS];

export const ONBOARDING_TARGET_ATTR = 'data-onboarding-id';

// Marker the current step's target carries so the global CSS paints the ring.
export const ONBOARDING_ACTIVE_ATTR = 'data-onboarding-active';

export const ACTIVE_TERMINAL_TAB_ATTR = 'data-terminal-active';

// Targets outside the terminal (Settings page, body-portalled popovers) can't
// be scoped by the active-tab marker.
const GLOBAL_TARGETS = new Set<OnboardingTarget>([
  ONBOARDING_TARGETS.settingsCredentialsCard,
  ONBOARDING_TARGETS.addCredentialButton,
  ONBOARDING_TARGETS.addTradingButton,
  ONBOARDING_TARGETS.connectWidgetsPopover,
  ONBOARDING_TARGETS.widgetTweaksPopover,
  ONBOARDING_TARGETS.editListModal,
  ONBOARDING_TARGETS.editListHighlight,
  ONBOARDING_TARGETS.marketMatchingModal,
  ONBOARDING_TARGETS.marketMatchingMatchedOnly,
]);

// Warm hidden tabs keep their DOM; scope in-terminal selectors to the active
// tab so we never spotlight an offscreen copy.
export const onboardingTargetSelector = (target: OnboardingTarget): string => {
  const base = `[${ONBOARDING_TARGET_ATTR}="${target}"]`;
  return GLOBAL_TARGETS.has(target)
    ? base
    : `[${ACTIVE_TERMINAL_TAB_ATTR}="true"] ${base}`;
};

// Controls that repeat across widgets (a news list's settings menu, say) are
// only unambiguous when scoped to the widget the tour spotlights.
export const onboardingNestedTargetSelector = (
  parent: OnboardingTarget,
  child: OnboardingTarget,
): string =>
  `${onboardingTargetSelector(parent)} [${ONBOARDING_TARGET_ATTR}="${child}"]`;

export const onboardingTargetProps = (target: OnboardingTarget) =>
  ({ [ONBOARDING_TARGET_ATTR]: target }) as Record<string, string>;
